import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './TraceTimeline.css';
import { formatAppDate, parseApiDate, isDateOnly } from '../../utils/dateUtils';

// performed_by placeholders the server sends when nobody is recorded
const PERFORMERS = { System: 'system', Unknown: 'unknown', Warehouse: 'warehouse' };

const TraceTimeline = ({ events, productionOrder, filters }) => {
  const { t } = useTranslation('supervisor');

  // Title/description of an event in the UI language (#386). The server sends `message_key` +
  // `params`; `title`/`description` (English) are the fallback for events without a key.
  const eventText = (event, part) => {
    if (!event.message_key) return event[part];
    const base = `traceTimeline.events.${event.message_key}`;
    const params = {};
    Object.entries(event.params || {}).forEach(([k, v]) => {
      params[k] = v ?? t(`traceTimeline.events.defaults.${k}`, { defaultValue: '—' });
    });
    return t(`${base}.${part}`, { ...params, defaultValue: event[part] });
  };

  const performerName = (name) =>
    PERFORMERS[name] ? t(`traceTimeline.performers.${PERFORMERS[name]}`) : name;

  // Filter events based on filters
  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let filtered = [...events];

    // Filter by event types
    if (filters.eventTypes && filters.eventTypes.length > 0) {
      filtered = filtered.filter(event =>
        filters.eventTypes.includes(event.category)
      );
    }

    // Filter by date range
    if (filters.dateRange?.start) {
      const startDate = parseApiDate(filters.dateRange.start);
      filtered = filtered.filter(event =>
        parseApiDate(event.timestamp) >= startDate
      );
    }

    if (filters.dateRange?.end) {
      const endDate = parseApiDate(filters.dateRange.end);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(event =>
        parseApiDate(event.timestamp) <= endDate
      );
    }

    // Filter by search term
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      // Search what the user sees (translated) and the original English text
      filtered = filtered.filter(event =>
        [eventText(event, 'title'), eventText(event, 'description'), event.title, event.description,
          event.performed_by, performerName(event.performed_by)]
          .some(text => (text || '').toLowerCase().includes(searchLower)) ||
        (event.metadata && JSON.stringify(event.metadata).toLowerCase().includes(searchLower))
      );
    }

    // Sort chronologically (earliest events first, top to bottom)
    filtered.sort((a, b) => parseApiDate(a.timestamp) - parseApiDate(b.timestamp));

    return filtered;
  }, [events, filters, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // Event category configuration
  const categoryConfig = {
    quote: {
      icon: '🎯',
      color: '#3498db',
      name: t('traceTimeline.categories.quote')
    },
    production: {
      icon: '🏭',
      color: '#27ae60',
      name: t('traceTimeline.categories.production')
    },
    material: {
      icon: '📦',
      color: '#f39c12',
      name: t('traceTimeline.categories.material')
    },
    task: {
      icon: '⚙️',
      color: '#9b59b6',
      name: t('traceTimeline.categories.task')
    },
    operator: {
      icon: '👷',
      color: '#8e44ad',
      name: t('traceTimeline.categories.operator')
    },
    inventory: {
      icon: '📋',
      color: '#16a085',
      name: t('traceTimeline.categories.inventory')
    },
    receipt: {
      icon: '✅',
      color: '#2ecc71',
      name: t('traceTimeline.categories.receipt')
    },
    fulfillment: {
      icon: '📤',
      color: '#e74c3c',
      name: t('traceTimeline.categories.fulfillment')
    },
    shipment: {
      icon: '🚚',
      color: '#c0392b',
      name: t('traceTimeline.categories.shipment')
    }
  };

  const getEventIcon = (category) => {
    return categoryConfig[category]?.icon || '⚡';
  };

  const getEventColor = (category) => {
    return categoryConfig[category]?.color || '#95a5a6';
  };

  const formatTimestamp = (timestamp) => {
    // Day-only events (start/end of the order, dispatch) have no time to show
    const date = parseApiDate(timestamp);
    return {
      date: formatAppDate(date),
      time: isDateOnly(timestamp) ? '' : formatAppDate(date, 'time')
    };
  };

  const getRelativeTime = (timestamp) => {
    const now = new Date();
    const eventTime = parseApiDate(timestamp);
    const diffMs = now - eventTime;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return diffDays === 1
        ? t('traceTimeline.relativeTime.dayAgo', { count: diffDays })
        : t('traceTimeline.relativeTime.daysAgo', { count: diffDays });
    } else if (diffHours > 0) {
      return diffHours === 1
        ? t('traceTimeline.relativeTime.hourAgo', { count: diffHours })
        : t('traceTimeline.relativeTime.hoursAgo', { count: diffHours });
    } else if (diffMinutes > 0) {
      return diffMinutes === 1
        ? t('traceTimeline.relativeTime.minuteAgo', { count: diffMinutes })
        : t('traceTimeline.relativeTime.minutesAgo', { count: diffMinutes });
    } else {
      return t('traceTimeline.relativeTime.justNow');
    }
  };


  const renderMetadata = (metadata) => {
    if (!metadata) return null;

    return (
      <div className="event-metadata">
        <div className="metadata-title">{t('traceTimeline.metadata.detailsTitle')}</div>
        <div className="metadata-content">
          {Object.entries(metadata).map(([key, value]) => {
            if (value === null || value === undefined || value === '') return null;

            return (
              <div key={key} className="metadata-item">
                <span className="metadata-key">
                  {t(`traceTimeline.metadata.keys.${key}`, { defaultValue: key.replace(/_/g, ' ') })}:
                </span>
                <span className="metadata-value">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!filteredEvents || filteredEvents.length === 0) {
    return (
      <div className="timeline-empty">
        <div className="empty-icon">📭</div>
        <div className="empty-title">{t('traceTimeline.emptyState.title')}</div>
        <div className="empty-description">
          {filters.eventTypes?.length > 0 || filters.searchTerm || filters.dateRange?.start
            ? t('traceTimeline.emptyState.descriptionFiltered')
            : t('traceTimeline.emptyState.descriptionEmpty')
          }
        </div>
      </div>
    );
  }

  return (
    <div className="trace-timeline">
      <div className="timeline-header">
        <h3>{t('traceTimeline.title')}</h3>
        <div className="timeline-stats">
          {filters.eventTypes?.length > 0 || filters.searchTerm || filters.dateRange?.start ? (
            <span className="filtered-count">
              {t('traceTimeline.stats.filteredCount', { filtered: filteredEvents.length, total: events.length })}
            </span>
          ) : (
            <span className="total-count">{t('traceTimeline.stats.totalCount', { count: filteredEvents.length })}</span>
          )}
        </div>
      </div>

      <div className="timeline-container">
        <div className="timeline-line"></div>
        <div className="timeline-edge timeline-edge-start">⏰ {t('traceTimeline.labels.earliestEvent')}</div>

        {filteredEvents.map((event, index) => {
          const timeInfo = formatTimestamp(event.timestamp);
          const eventColor = getEventColor(event.category);

          return (
            <div key={index} className="timeline-event">
              <div
                className="event-marker"
                style={{ backgroundColor: eventColor }}
              >
                <span className="event-icon">{getEventIcon(event.category)}</span>
              </div>

              <div className="event-content">
                <div className="event-header">
                  <div className="event-title">{eventText(event, 'title')}</div>
                  <div className="event-time">
                    <span className="event-date">{timeInfo.date}</span>
                    <span className="event-time-detail">{timeInfo.time}</span>
                    <span className="event-relative">{getRelativeTime(event.timestamp)}</span>
                  </div>
                </div>

                <div className="event-description">{eventText(event, 'description')}</div>

                <div className="event-meta">
                  <div className="event-category">
                    <span
                      className="category-badge"
                      style={{ backgroundColor: eventColor }}
                    >
                      {categoryConfig[event.category]?.name || event.category}
                    </span>
                  </div>
                  <div className="event-performer">
                    {t('traceTimeline.labels.performedBy')} {performerName(event.performed_by)}
                  </div>
                </div>

                {/* Always show metadata directly if it exists */}
                {event.metadata && Object.keys(event.metadata).length > 0 && renderMetadata(event.metadata)}
              </div>
            </div>
          );
        })}
        <div className="timeline-edge timeline-edge-end">🏁 {t('traceTimeline.labels.latestEvent')}</div>
      </div>

    </div>
  );
};

export default TraceTimeline;